import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FieldType } from "../../types/Interface";
import { WSConfigProductData, WSConfigCombinationIfaceData } from "../../types/WSConfigData";
import { WSCInputCompletionChip } from "./WSCInputCompletionChip";
import { WorkshopProductPairProperty } from "../../types/WSTestingData";
import { InterfaceProperty } from "../../types/Interface";
import { WorkshopProduct } from "../../types/WorkshopProducts";
import { WorkshopProductProperty, updateWorkshopproductProperty } from "../../types/WorkshopProductProperty";
import { PropertyJSONErrorModal } from "../../modals/PropertyJSONErrorModal";
import { FolderLabel } from '../../subs/FolderLabel';
import { AlertProps } from '@mui/material/Alert';

import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField } from "@mui/material";

import Grid from '@mui/material/Grid2';
import { copyArrayWithIndexChanged, groupBy } from "../../Utils";

import { useSnackbar } from '../../../contexts/SnackBarContext';

export const WSCiFaceAccordion = ({ ifaceDataIn, wsConfigProductData, setWsConfigProductData, refreshProductData }:
    {
        ifaceDataIn: WSConfigCombinationIfaceData[], wsConfigProductData: WSConfigProductData,
        setWsConfigProductData: React.Dispatch<React.SetStateAction<WSConfigProductData | undefined>>,
        refreshProductData: (newlyMarkedTestCases?: number) => void
    }) => {
    const { showSnackbar } = useSnackbar();
    const [ifaceData, setIfaceData] = React.useState<WSConfigCombinationIfaceData[]>([]);
    const [interfaceProperty, setInterfaceProperty] = React.useState<InterfaceProperty>();
    const [workshopProduct, setWorkshopProduct] = React.useState<WorkshopProduct>();
    const [workshopProductProperty, setWorkshopProductProperty] = React.useState<WorkshopProductProperty>();
    const [jsonErrorModalOpenState, setjsonErrorModalOpenState] = React.useState<boolean>(false);
    const [lockButtons, setLockButtons] = React.useState<boolean>(false);
    const [jsonErrorModalInterfaceId, setjsonErrorModalInterfaceId] = React.useState<number>(0);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        setIfaceData(ifaceDataIn);
    }, [ifaceDataIn])

    React.useEffect(() => {
        setjsonErrorModalOpenState(false);
        //if (disabled) return;    // if the form is disabled, we don't want to show the json error modal even if there are json errors, since the user can't do anything about them
        var BreakException = {};        // react limitation doesn't allow to break out of a forEach loop, so we throw an exception and catch it immediately to break the loop when we find a json error
        try {
            ifaceDataIn.forEach(iface => {
                iface.properties.forEach(wpp => {
                    if (wpp && wpp.jsonErrorValue && wpp.value !== '') {
                        const workshopProduct: WorkshopProduct = {
                            id: wsConfigProductData.key.workshopProductId,
                            productId: wsConfigProductData.productId,
                            workshopId: wsConfigProductData.workshopId,
                            status: wsConfigProductData.status,
                            productVersion: wsConfigProductData.productVersion,
                            releaseStatus: wsConfigProductData.releaseStatus,
                            description: wsConfigProductData.description,
                            isServer: false,
                            isClient: false,
                            clientReadyToTest: false,
                            serverReadyToTest: false,
                        }
                        setWorkshopProduct(workshopProduct);

                        const interfaceProperty: InterfaceProperty = {
                            id: wpp.interfacePropertyId,
                            interfaceId: iface.interfaceId,
                            group: wpp.group,
                            name: wpp.name,
                            fieldType: wpp.fieldTypeVal,
                            fieldSchema: wpp.fieldSchema
                        }
                        setInterfaceProperty(interfaceProperty);

                        const workshopProductProperty: WorkshopProductProperty = {
                            workshopProductId: wsConfigProductData.key.workshopProductId,
                            interfacePropertyId: wpp.interfacePropertyId,
                            testCombinationId: wsConfigProductData.key.testCombinationId,
                            value: wpp.value,
                            id: wpp.wppId,
                            jsonError: true
                        }
                        setWorkshopProductProperty(workshopProductProperty);
                        setjsonErrorModalInterfaceId(iface.interfaceId);

                        setjsonErrorModalOpenState(true);
                        throw BreakException;
                    }
                });
            });
        } catch (error) {
            if (error !== BreakException) showUserFeedback(String(error));
        }
    }, [ifaceDataIn, wsConfigProductData, showUserFeedback])

    const workshopProductPairPropertySubmitHandler = async (wpp: WorkshopProductPairProperty) => {
        if (wpp.interfacePropertyId === undefined) return;
        setLockButtons(true);

        let ifaceDataLoc: WSConfigCombinationIfaceData[] = [ ...ifaceData ];
        let iface = ifaceDataLoc.find(item => item.interfaceId === wpp.interfaceId);
        if (iface === undefined) return;

        let property = iface.properties.find(item => item.interfacePropertyId === wpp.interfacePropertyId);
        if (property === undefined) return;

        const productProperty: WorkshopProductProperty = {
            id: wpp.wppId,
            interfacePropertyId: wpp.interfacePropertyId,
            workshopProductId: wsConfigProductData.key.workshopProductId,
            testCombinationId: wsConfigProductData.key.testCombinationId,
            value: wpp.value?.trim() ?? '',
        }

        let response = await updateWorkshopproductProperty(productProperty);

        if (response.failed) {
            showUserFeedback(response.errorText);
            return;
        }
        refreshProductData();

        showUserFeedback("Product property support updated.", 'info');
        setLockButtons(false);
    }

    const workshopProductPropertySubmitHandler = async (wpp: WorkshopProductProperty) => {
        if (wpp.interfacePropertyId === undefined) return;
        if (jsonErrorModalInterfaceId === undefined) return;

        let response = await updateWorkshopproductProperty(wpp);

        if (response.failed) {
            showUserFeedback(response.errorText);
            return;
        }

        let ifaceDataLoc = [...ifaceData];
        let iface = ifaceDataLoc.find(item => item.interfaceId === jsonErrorModalInterfaceId);
        if (iface === undefined) return;

        let property = iface.properties.find(item => item.interfacePropertyId === wpp.interfacePropertyId);
        if (property === undefined) return;

        let updatedProp = response.result;
        property.value = updatedProp.value;

        copyArrayWithIndexChanged(iface.properties, iface.properties.findIndex(item => item.interfacePropertyId === wpp.interfacePropertyId), property);
        copyArrayWithIndexChanged(ifaceDataLoc, ifaceDataLoc.findIndex(item => item.interfaceId === jsonErrorModalInterfaceId), iface);
        setIfaceData(ifaceDataLoc);

        let wsConfigProductDataCopy = { ...wsConfigProductData, combinationIfaces: ifaceDataLoc };
        setWsConfigProductData(wsConfigProductDataCopy);

        showUserFeedback("Product property support updated.", 'info');
        refreshProductData();
    }

    return <>
        <PropertyJSONErrorModal
            openState={jsonErrorModalOpenState} setOpenState={setjsonErrorModalOpenState}
            interfaceProperty={interfaceProperty}
            workshopProduct={workshopProduct}
            workshopProductProperty={workshopProductProperty}
            workshopProductPropertySubmitHandler={workshopProductPropertySubmitHandler} />
        {
            ifaceData.length > 0 && ifaceData.map((iface, index) => {
                return iface.supportedByProduct ?
                    <WSCiFaceGroupTopAccordion ifaceDataIn={iface} lockButtons={lockButtons}
                        workshopProductPairPropertySubmitHandler={workshopProductPairPropertySubmitHandler} />
                    : <></>
        })}
    </>
}

export const WSCiFaceGroupTopAccordion = ({ ifaceDataIn, lockButtons, workshopProductPairPropertySubmitHandler }:
    {
        ifaceDataIn: WSConfigCombinationIfaceData, 
        lockButtons: boolean,
        workshopProductPairPropertySubmitHandler: (wpp: WorkshopProductPairProperty) => void
    }) => {
    const [ifaceData, setIfaceData] = React.useState<WSConfigCombinationIfaceData>(ifaceDataIn);
    const [propertiesComplete, setPropertiesComplete] = React.useState<number>(0);
    const [propertiesIncomplete, setPropertiesIncomplete] = React.useState<number>(0);
    const [propertiesOptional, setPropertiesOptional] = React.useState<number>(0);
    const [propertiesAllComplete, setPropertiesAllComplete] = React.useState<boolean>(false);

    React.useEffect(() => {
        setIfaceData(ifaceDataIn);
    }, [ifaceDataIn])

    return <>
        <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                    <FolderLabel labelText={('Server Properties: ') + ifaceData.interfaceName} labelVariant='large' noicon={true}
                        passed={propertiesComplete} info={propertiesOptional} errors={propertiesIncomplete}
                    />
                    <WSCInputCompletionChip completed={propertiesAllComplete} />
                </Stack>
            </AccordionSummary>

            <AccordionDetails>
                <WSCiFaceGroupAccordion workshopProductProps={ifaceData.properties} lockButtons={lockButtons}
                    setPropertiesComplete={setPropertiesComplete} setPropertiesIncomplete={setPropertiesIncomplete}
                    setPropertiesOptional={setPropertiesOptional} setPropertiesAllComplete={setPropertiesAllComplete}
                    workshopProductPairPropertySubmitHandler={workshopProductPairPropertySubmitHandler} />
            </AccordionDetails>
        </Accordion>
    </>
}

export const WSCiFaceGroupAccordion = ({ workshopProductProps, setPropertiesComplete, setPropertiesIncomplete, lockButtons,
    setPropertiesOptional, setPropertiesAllComplete, workshopProductPairPropertySubmitHandler }:
    {
        workshopProductProps: WorkshopProductPairProperty[],
        lockButtons: boolean,
        setPropertiesComplete: React.Dispatch<React.SetStateAction<number>>,
        setPropertiesIncomplete: React.Dispatch<React.SetStateAction<number>>,
        setPropertiesOptional: React.Dispatch<React.SetStateAction<number>>,
        setPropertiesAllComplete: React.Dispatch<React.SetStateAction<boolean>>,
        workshopProductPairPropertySubmitHandler: (wpp: WorkshopProductPairProperty) => void
    }) => {
    const ifacePropGroups: { [key: string]: WorkshopProductPairProperty[] } = groupBy(workshopProductProps, ip => ip.group);
    const groupKeys = [...Object.keys(ifacePropGroups)];

    React.useEffect(() => {
        let requiredProperties: number = 0;
        let optionalProperties: number = 0;
        let completeProperties: number = 0;

            workshopProductProps.forEach(ip => {
                if (ip.value === '' || ip.value === undefined || ip.value === null) {
                    if (ip.fieldSchemaObject === null) {
                        if (ip.fieldTypeVal === FieldType.CheckBox) {
                            requiredProperties++;
                        }
                        else optionalProperties++;
                    }
                    else {
                        if (ip.fieldSchemaObject.isRequired) {
                            requiredProperties++;
                        }
                        else optionalProperties++;
                    }
                }
                else {
                    completeProperties++;
                }
            });
        setPropertiesComplete(completeProperties);
        setPropertiesIncomplete(requiredProperties);
        setPropertiesOptional(optionalProperties);
        setPropertiesAllComplete(requiredProperties === 0);

    }, [ workshopProductProps, setPropertiesComplete, setPropertiesIncomplete, setPropertiesOptional, setPropertiesAllComplete])

    return (workshopProductProps.length === 0) ? <></> : <>
        {groupKeys.map((key, index) => {
            return <WSCSubGroupAccordion workshopProductProps={ifacePropGroups[groupKeys[index]]} lockButtons={lockButtons}
                groupName={groupKeys[index]} workshopProductPairPropertySubmitHandler={workshopProductPairPropertySubmitHandler} />
        }
        )}
    </>
}

export const WSCSubGroupAccordion = ({ workshopProductProps, groupName, lockButtons, workshopProductPairPropertySubmitHandler }:
    {
        workshopProductProps: WorkshopProductPairProperty[], groupName: string, lockButtons: boolean,
        workshopProductPairPropertySubmitHandler: (wpp: WorkshopProductPairProperty) => void
    }) => {
    const [propertiesComplete, setPropertiesComplete] = React.useState<number>(0);
    const [propertiesIncomplete, setPropertiesIncomplete] = React.useState<number>(0);
    const [propertiesOptional, setPropertiesOptional] = React.useState<number>(0);

    React.useEffect(() => {
        let requiredProperties: number = 0;
        let optionalProperties: number = 0;
        let completeProperties: number = 0;

        workshopProductProps.forEach(ip => {
            if (ip.value === '' || ip.value === undefined || ip.value === null) {
                if (ip.fieldSchemaObject === null) {
                    if (ip.fieldTypeVal === FieldType.CheckBox) {
                        requiredProperties++;
                    }
                    else optionalProperties++;
                }
                else {
                    if (ip.fieldSchemaObject.isRequired) {
                        requiredProperties++;
                    }
                    else optionalProperties++;
                }
            }
            else {
                completeProperties++;
            }
        });
        setPropertiesComplete(completeProperties);
        setPropertiesIncomplete(requiredProperties);
        setPropertiesOptional(optionalProperties);

    }, [workshopProductProps])

    // some interfaces don't contain any groups
    return (workshopProductProps.length === 0) ? <></> : <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                <Typography>{groupName}</Typography>
                {<FolderLabel labelText={''} labelVariant='large' noicon={true}
                    passed={propertiesComplete} info={propertiesOptional} errors={propertiesIncomplete} />}
            </Stack>
        </AccordionSummary>

        <AccordionDetails>
            <form>
                <fieldset disabled={false} >
                    <Grid container rowGap={10}>
                        {workshopProductProps.map(workshopProductProp => {
                            return <Grid size={{ xs: workshopProductProp.fieldTypeVal === FieldType.Table || workshopProductProp.fieldTypeVal === FieldType.MultiLineBox ? 12 : 4 }} columnGap={5}>
                                {<WSCPropertyInput workshopProductProperty={workshopProductProp} lockButtons={lockButtons}
                                    workshopProductPairPropertySubmitHandler={workshopProductPairPropertySubmitHandler} />}
                            </Grid>
                        })}
                    </Grid>
                </fieldset>
            </form>
        </AccordionDetails>
    </Accordion>
}

export const WSCPropertyInput = ({ workshopProductProperty, lockButtons, workshopProductPairPropertySubmitHandler }:
    {
        workshopProductProperty: WorkshopProductPairProperty,
        lockButtons: boolean,
        workshopProductPairPropertySubmitHandler: (wpp: WorkshopProductPairProperty) => void
    }): React.ReactElement => {
    let inner: React.ReactElement = <></>;

    const [inputContents, setInputContents] = React.useState<string>('');

    React.useEffect(() => {
        setInputContents(workshopProductProperty?.value ?? '')
    }, [workshopProductProperty]);

    const handlepropertySubmit = (e: React.FocusEvent<HTMLInputElement>) => {
        workshopProductPairPropertySubmitHandler({ ...workshopProductProperty, value: e.target.value });
    }

    switch (workshopProductProperty.fieldTypeVal) {
        case FieldType.CheckBox:
            inner = <WSCPropertyInputRadioGroup workshopProductProperty={workshopProductProperty} workshopProductPairPropertySubmitHandler={workshopProductPairPropertySubmitHandler} />
            break;
        case FieldType.MultiLineBox:
            console.log('MultiLineTextBox: ', workshopProductProperty.fieldSchemaObject ?? JSON.stringify(workshopProductProperty.fieldSchemaObject));
            inner = <TextField multiline
                label={<Typography color={'black'} variant='body2'>
                    {workshopProductProperty.name}
                </Typography>}
                slotProps={{ inputLabel: { shrink: true }, }}
                maxRows={workshopProductProperty.fieldSchemaObject?.maxRows ?? 0}
                minRows={workshopProductProperty.fieldSchemaObject?.minRows ?? 0}
                value={inputContents}
                onChange={(e) => setInputContents(e.target.value)}
                onBlur={handlepropertySubmit} />
            break;
        case FieldType.Table:
            inner = workshopProductProperty.fieldSchemaObject ? <WSCEditableTable workshopProductProperty={workshopProductProperty}
                lockButtons={lockButtons} workshopProductPairPropertySubmitHandler={workshopProductPairPropertySubmitHandler} />
                : <></>
            break;
        case FieldType.TextBox:
            inner = <TextField
                label={<Typography color={'black'} variant='body2'>
                    {workshopProductProperty.name}
                </Typography>}
                defaultValue={workshopProductProperty?.value ?? ''} slotProps={{ inputLabel: { shrink: true } }}></TextField>
            break;
    }

    return <FormControl fullWidth>
        {[inner]}
    </FormControl>
}

export const WSCPropertyInputRadioGroup = ({ workshopProductProperty, workshopProductPairPropertySubmitHandler }:
    {
        workshopProductProperty: WorkshopProductPairProperty,
        workshopProductPairPropertySubmitHandler: (wpp: WorkshopProductPairProperty) => void
    }): React.ReactElement => {
    const [value, setValue] = React.useState('');

    React.useEffect(() => {
        setValue(workshopProductProperty?.value ?? '');
    }, [workshopProductProperty]);

    const handlepropertyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
        workshopProductPairPropertySubmitHandler({ ...workshopProductProperty, value: e.target.value })
    }

    return <FormControl fullWidth>
        <FormLabel id={"prooertyAsRadioGroup" + workshopProductProperty.interfacePropertyId}
            sx={{ color:'black' }}
            color={'info'}>
            {workshopProductProperty.name}
        </FormLabel>
        <RadioGroup value={value} aria-labelledby={"prooertyAsRadioGroup" + workshopProductProperty.interfacePropertyId}
            onChange={handlepropertyChange} name="prooertyAsTrueFalse">
            <FormControlLabel value={'-1'} label={'Yes'} control={<Radio />} />
            <FormControlLabel value={'0'} label={'No'} control={<Radio />} />
        </RadioGroup>
    </FormControl>
}

const newEmptyRow = (columnDefinitons: GridColDef[], id: number) => {
    const rowObject: { [key: string]: any } = { id: id };

    columnDefinitons.forEach((columnDefiniton, index) => {
        rowObject[columnDefiniton.field] = '';
    });

    return rowObject;
}

const parseTableRows = (columnDefinitions: GridColDef[], rows?: Array<string[]>) => {
    const rowObjects: { [key: string]: any }[] = [];

    if (!rows || !Array.isArray(rows)) return rowObjects;

    rows.map((row, index) => {
        let rowObject: { [key: string]: any } = newEmptyRow(columnDefinitions, index);

        columnDefinitions.forEach((columnDefinition, index) =>
            rowObject[columnDefinition.field] = row[index]);

        rowObjects.push(rowObject);
        return null;    // map requires a return, but we don't actually need to return anything here
    })

    return rowObjects;
}

const removeRow = (rows: Array<{ [key: string]: any }>, id: number) => rows.filter(row => row.id !== id);

const updateRow = (rows: Array<{ [key: string]: any }>, updatedRow: { [key: string]: any }) => {
    const rowIndex = rows.findIndex(row => row.id === updatedRow.id);

    console.log('updateRow updatedRow: ', updatedRow);
    console.log('updateRow rowIndex: ', rowIndex);
    console.log('updateRow: ', [...rows.slice(0, rowIndex), updatedRow, ...rows.slice(rowIndex + 1, rows.length)])

    return [...rows.slice(0, rowIndex), updatedRow, ...rows.slice(rowIndex + 1, rows.length)];
}

// Build a safe JSON string for the table: array-of-arrays. Use JSON.stringify to properly escape values.
const tableRows2String = (rows: Array<{ [key: string]: any }>) => {
    // For each row object, take values excluding the leading 'id' and the trailing action column ('deleteRow') if present.
    const arr = rows.map(row => {
        const rowValues = Object.values(row);
        // slice out id (first) and last column (actions) which is typically the delete column.
        const valuesOnly = rowValues.slice(1, rowValues.length - 1);
        return valuesOnly.map(v => (v === undefined || v === null) ? '' : String(v));
    });
    return JSON.stringify(arr);
}

const WSCEditableTable = ({ workshopProductProperty, lockButtons, workshopProductPairPropertySubmitHandler }:
    {
        workshopProductProperty: WorkshopProductPairProperty,
        lockButtons: boolean,
        workshopProductPairPropertySubmitHandler: (wpp: WorkshopProductPairProperty) => void
    }) => {
    const [tableRows, setTableRows] = React.useState<{ [key: string]: any }[]>([]);   // there is no need to initialize this, as it will be set in useEffect
    const [tableActiveRows, setTableActiveRows] = React.useState<{ [key: string]: any }[]>([]);   // there is no need to initialize this, as it will be set in useEffect

    console.log('json: ', workshopProductProperty?.value);

    const columnDefintions: GridColDef[] =
        (workshopProductProperty && workshopProductProperty.fieldSchemaObject && Array.isArray(workshopProductProperty.fieldSchemaObject.columns)) ?
            workshopProductProperty.fieldSchemaObject.columns.map((column: string) => {
                return {
                    field: column,
                    headerName: column,
                    flex: 1,
                    editable: true,
                }
            }) : [];

    const deleteActionColumn: GridColDef = {
        field: 'deleteRow',
        type: "actions",
        renderHeader: () => <GridActionsCellItem disabled={lockButtons} color="inherit" icon={<AddIcon />} label={'Add row'} onClick={() => { addRow(); }} />,
        getActions: ({ id }) => [<GridActionsCellItem disabled={lockButtons} icon={<DeleteIcon />} label={'Cancel'} onClick={() => { deleteRow(Number(id)); }}
            color='inherit' />]
    }
    columnDefintions.push(deleteActionColumn);

    // Safely parse stored JSON for the table. If the stored value is invalid JSON, log a warning and use an empty table.
    React.useEffect(() => {
        setTableRows(parseTableRows(columnDefintions, workshopProductProperty.rows ?? []));
    }, [workshopProductProperty])       // columnDefintions changes every render, so including here creates a loop... find cleaner way to break this

    React.useEffect(() => {
        if (lockButtons === false) { // if buttons are clicked while saving - the post save refresh can error - prevent the update until saving is complete
            setTableActiveRows(tableRows);
        }
    }, [tableRows, lockButtons])       // columnDefintions changes every render, so including here creates a loop... find cleaner way to break this loop

    const addRow = () => {
        if (tableActiveRows.length >= (workshopProductProperty.fieldSchemaObject?.maxRows ?? 0)) return;
        const nextRowId = tableActiveRows.length > 0 ? Math.max(...tableActiveRows.map(row => row.id)) + 1 : 1; // the previous method of counting rows by length is not reliable, as rows can be deleted
        setTableActiveRows([...tableActiveRows, newEmptyRow(columnDefintions, nextRowId)]);
    }

    const deleteRow = (id: number) => {
//        if (tableActiveRows.length <= (workshopProductProperty.fieldSchemaObject?.minRows ?? 0) && workshopProductProperty.fieldSchemaObject?.isRequired) return;
        const updatedTableRows = removeRow(tableActiveRows, id);
        workshopProductPairPropertySubmitHandler({ ...workshopProductProperty, value: tableRows2String(updatedTableRows) });
        setTableActiveRows(updatedTableRows);
    }

    const onRowUpdate = (newRow: { [key: string]: any }, oldRow: { [key: string]: any }) => {
        const updatedTableRows = updateRow(tableActiveRows, newRow);
        workshopProductPairPropertySubmitHandler({ ...workshopProductProperty, value: tableRows2String(updatedTableRows) });
        setTableActiveRows(updatedTableRows);
        console.log('TableRows: ', tableRows2String(updatedTableRows));
        console.log('UpdatedTableRows: ', updatedTableRows);
        return newRow;
    }

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Box
            sx={{
                '& .required-theme--header': {
                    color: 'rgba(255, 0, 0, 1)',
                },
                '& .optional-theme--header': {
                    color: 'rgba(0, 0, 0, 1)',
                },
            }}
        >
            <Typography variant="body1" color="error" sx={{ mb: 1 }}>{workshopProductProperty?.jsonErrorValue ? "*Invalid JSON data, please re-enter the data. (note: the errored data will be removed)" : ""}</Typography>
            <DataGrid
                columns={columnDefintions}
                rows={tableActiveRows}
                showColumnVerticalBorder
                showCellVerticalBorder
                hideFooter
                processRowUpdate={onRowUpdate}
                onProcessRowUpdateError={(error) => { console.log('ERROR: ', error); }}
                getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'}
                editMode='row' />
        </Box>
    </div>
}
